"use client";

import { useMemo, useState } from "react";
import { calcDrawdown } from "@/lib/tools/drawdown";
import { formatUsd } from "@/lib/utils";
import { Field, NumberInput, Out } from "@/components/tools/form-controls";

export function DrawdownClient() {
  const [accountSize, setAccountSize] = useState(100_000);
  const [maxDD, setMaxDD] = useState(10);
  const [hasDaily, setHasDaily] = useState(true);
  const [dailyDD, setDailyDD] = useState(5);

  const result = useMemo(
    () =>
      calcDrawdown({
        accountSizeUsd: accountSize,
        maxDrawdownPct: maxDD,
        dailyDrawdownPct: hasDaily ? dailyDD : null,
      }),
    [accountSize, maxDD, hasDaily, dailyDD],
  );

  return (
    <div className="space-y-6">
      <Field label="Account size (USD)">
        <NumberInput value={accountSize} onChange={setAccountSize} step={1000} min={100} />
      </Field>
      <Field label="Max drawdown (%)">
        <NumberInput value={maxDD} onChange={setMaxDD} step={0.5} min={1} max={50} />
      </Field>
      <label className="flex items-center gap-3 text-sm">
        <input
          type="checkbox"
          checked={hasDaily}
          onChange={(e) => setHasDaily(e.target.checked)}
          className="size-4 rounded border-[var(--border)] bg-[var(--bg)]"
        />
        Firm enforces daily drawdown
      </label>
      {hasDaily ? (
        <Field label="Daily drawdown (%)">
          <NumberInput value={dailyDD} onChange={setDailyDD} step={0.5} min={1} max={20} />
        </Field>
      ) : null}

      <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-6">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Out label="Max DD in $" value={formatUsd(result.maxDrawdownUsd)} highlight />
          <Out
            label="Daily DD in $"
            value={result.dailyDrawdownUsd == null ? "—" : formatUsd(result.dailyDrawdownUsd)}
          />
          <Out label="1% losses to bust" value={`${result.riskBudgetAt1Pct}`} highlight />
          <Out label="Total loss room" value={`${result.breakEvenStreakLossPct}%`} />
        </div>
        <p className="mt-4 text-sm text-[var(--fg-muted)] leading-relaxed">
          Read: you can lose {result.riskBudgetAt1Pct} trades of 1% before your account is busted under
          this firm&apos;s static drawdown. Trailing drawdown firms tighten this number after every winning streak.
        </p>
      </div>
    </div>
  );
}
